// Deliveries: every order on a day's sent routes at a warehouse, as Samsara
// reports it. One row per order, truck by truck: its stage (scheduled, left
// depot, at the drop, then an outcome), anything to look at, and its proof of
// delivery. Refresh from Samsara reads the day now (and once by itself when
// today's tab opens stale); each night, finished routes are closed out. An
// order opens to its timeline, stamped photos and share link, and there the
// dispatcher can upload the proof a driver didn't give, and record its outcome
// over Samsara's, with why (or go back to Samsara's). A failed attempt has an
// exception code and a next step for its freight (its follow-up); the open
// follow-ups of every day are one list of their own. Each attempt's history is
// a timeline of who changed what. A 3PL carrier's orders sit in the same list:
// Samsara doesn't see them, so the dispatcher records what the carrier
// reports, order by order or for a whole trip. The day's 3PL trucks still without a carrier are named, each opening
// its plan at that truck, as an order opens its plan at its truck. Above the
// orders, each driver's clock in and out: their start- and end-of-shift
// inspections in Samsara. A plan sends
// the dispatcher here on its own depot and day. The day can be narrowed by carrier, status and missing proof, and
// exports as CSV. A search finds an order by its number, a pallet id or
// uShipment's order id across every day and warehouse, with every attempt to
// deliver it; its rows are the day's rows, and clearing it brings the day back.

import {
  type CSSProperties,
  createContext,
  Fragment,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  deleteDeliveryProof,
  downloadDeliveries,
  fetchDeliveryPhoto,
  getExceptionCodes,
  getOrderHistory,
  listDeliveries,
  listFollowUps,
  recodeDelivery,
  recordOutcome,
  recordTripUpdate,
  refreshDeliveries,
  renewShareLink,
  revertOutcome,
  reviewDelivery,
  searchDeliveries,
  setDisposition,
  uploadDeliveryProof,
} from "../api/client";
import { clockOnDay, instantOnDay, todayIn, zoneName } from "../lib/format";
import { type MsgKey, useLang, useT } from "../lib/i18n";
import type {
  DeliveriesResponse,
  DeliveryOutcome,
  DeliveryRecord,
  DeliveryStage,
  DeliverySummary,
  DeliverySearch,
  Disposition,
  DriverShift,
  ExceptionCode,
  FollowUps,
  ManualStage,
  OrderHistory,
  ProofPhoto,
  RefreshedDeliveries,
  SearchAttempt,
  StatusGroup,
  StatusSource,
} from "../types";

// The warehouses, as in dispatch.config.regions.
const DEPOTS = [
  { zip: "90802", region: "region.socal" },
  { zip: "08831", region: "region.east" },
] as const;

const SUMMARY: (keyof DeliverySummary)[] = [
  "orders",
  "in_progress",
  "delivered",
  "exceptions",
  "not_delivered",
  "on_time",
  "pod_missing",
  "needs_attention",
  "follow_ups",
];

const STAGE_STYLE: Record<DeliveryStage, CSSProperties> = {
  scheduled: { background: "rgba(0,0,0,0.06)", color: "inherit" },
  left_depot: { background: "rgba(37,99,235,0.12)", color: "#1d4ed8" },
  at_drop: { background: "rgba(124,58,237,0.12)", color: "#6d28d9" },
  delivered: { background: "rgba(22,163,74,0.14)", color: "#15803d" },
  delivery_exception: { background: "rgba(234,88,12,0.14)", color: "#c2410c" },
  not_delivered: { background: "rgba(232,72,85,0.12)", color: "#a4161a" },
};

const STAGES = Object.keys(STAGE_STYLE) as DeliveryStage[];

// The stages in each status group (order_status.GROUPS): the stage filter's choices.
const GROUP_STAGES: Record<StatusGroup, DeliveryStage[]> = {
  pending: ["scheduled"],
  in_transit: ["left_depot", "at_drop"],
  completed: ["delivered"],
  exception: ["delivery_exception", "not_delivered"],
};
const GROUPS = Object.keys(GROUP_STAGES) as StatusGroup[];

// What a 3PL carrier may report for a whole trip; a problem or a non-delivery needs a note.
const MANUAL_STAGES: ManualStage[] = [
  "left_depot",
  "delivered",
  "delivery_exception",
  "not_delivered",
  "scheduled",
];
const NEEDS_NOTE: ManualStage[] = ["delivery_exception", "not_delivered", "scheduled"];

const OUTCOMES: DeliveryOutcome[] = ["delivered", "delivery_exception", "not_delivered"];
const FAILED: DeliveryStage[] = ["delivery_exception", "not_delivered"];
const SOURCES: StatusSource[] = ["samsara", "carrier", "dispatcher"];
// What happens to a failed attempt's freight (delivery_status.DISPOSITIONS);
// the first five keep its follow-up open.
const DISPOSITIONS: Disposition[] = [
  "awaiting_decision",
  "redeliver",
  "returning_to_warehouse",
  "at_warehouse",
  "customer_pickup",
  "picked_up",
  "cancelled",
];
const OPEN_DISPOSITIONS = DISPOSITIONS.slice(0, 5);

const COLUMNS = 11;

// The exception catalogue, read once for the whole page.
const Codes = createContext<ExceptionCode[]>([]);

// A code's label in the page's language; the code itself if the catalogue hasn't it.
function useCodeLabel(): (code: string | null) => string {
  const codes = useContext(Codes);
  const { lang } = useLang();
  return (code) => {
    const c = codes.find((x) => x.code === code);
    return c ? (lang === "zh" ? c.label_zh : c.label) : (code ?? "");
  };
}

// What a dispatcher may upload, and what of it proves a delivery (as the API).
const UPLOAD_KINDS = ["signed_bol", "pallets", "issue", "other"] as const;
const PROOF_KINDS: string[] = ["signed_bol", "pallets"];

export function DeliveriesView({
  onOpenPlan,
  initial,
}: {
  onOpenPlan: (planId: string, truckId?: string) => void;
  // The depot and day to open on (from a plan); else the first depot's latest day.
  initial?: { depotZip: string; planDate: string };
}) {
  const t = useT();
  const { lang } = useLang();
  const [depotZip, setDepotZip] = useState<string>(initial?.depotZip ?? DEPOTS[0].zip);
  const [planDate, setPlanDate] = useState<string | undefined>(initial?.planDate);
  const [day, setDay] = useState<DeliveriesResponse | null>(null);
  const [attentionOnly, setAttentionOnly] = useState(false);
  // "", "fleet", "3pl" or one carrier's id.
  const [carriedBy, setCarriedBy] = useState("");
  const [stage, setStage] = useState<DeliveryStage | "">("");
  const [podMissing, setPodMissing] = useState(false);
  const [group, setGroup] = useState<StatusGroup | "">("");
  const [code, setCode] = useState("");
  const [source, setSource] = useState<StatusSource | "">("");
  // The open follow-ups of every day in place of the day's orders.
  const [followUps, setFollowUps] = useState(false);
  const [codes, setCodes] = useState<ExceptionCode[]>([]);
  // The search box, and what the last search found (null: the day view).
  const [query, setQuery] = useState("");
  const [found, setFound] = useState<DeliverySearch | null>(null);
  const [searching, setSearching] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [failures, setFailures] = useState<RefreshedDeliveries["refresh"]["failures"]>([]);
  const [simulated, setSimulated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh(date: string) {
    setRefreshing(true);
    setError(null);
    try {
      const d = await refreshDeliveries(date, depotZip);
      setDay(d);
      setFailures(d.refresh.failures);
      setSimulated(d.refresh.simulated);
    } catch (e) {
      setError(t("deliveries.refreshFailed", { msg: e instanceof Error ? e.message : String(e) }));
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    // Without the catalogue a code shows as itself and the pickers are empty.
    getExceptionCodes()
      .then(setCodes)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    let alive = true;
    setError(null);
    setFailures([]);
    setSimulated(false);
    listDeliveries({ depotZip, planDate })
      .then((d) => {
        if (!alive) return;
        setDay(d);
        // Today's tab opens on what Samsara says now, not on a stale read.
        if (d.refresh_due && d.plan_date) void refresh(d.plan_date);
      })
      .catch((e) => {
        if (alive) setError(t("deliveries.loadFailed", { msg: e instanceof Error ? e.message : String(e) }));
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depotZip, planDate]);

  // The day again after the dispatcher changed a record's proof.
  async function reload() {
    if (!day?.plan_date) return;
    try {
      setDay(await listDeliveries({ depotZip, planDate: day.plan_date }));
    } catch (e) {
      setError(t("deliveries.loadFailed", { msg: e instanceof Error ? e.message : String(e) }));
    }
  }

  const shown = (r: DeliveryRecord) =>
    (!attentionOnly || r.attention.length > 0) &&
    (!carriedBy || r.carrier_type === carriedBy || r.carrier_id === carriedBy) &&
    (!group || r.status_group === group) &&
    (!stage || r.stage === stage) &&
    (!podMissing || r.flags.includes("no_pod")) &&
    (!code || r.exception_code === code) &&
    (!source || r.status_source === source);
  const records = (day?.records ?? []).filter(shown);
  // The stage filter narrows the group's stages.
  const stages = group ? GROUP_STAGES[group] : STAGES;

  async function runSearch(q: string) {
    if (!q.trim()) return clearSearch();
    setSearching(true);
    setError(null);
    try {
      setFound(await searchDeliveries(q.trim()));
    } catch (e) {
      setError(t("deliveries.searchFailed", { msg: e instanceof Error ? e.message : String(e) }));
    } finally {
      setSearching(false);
    }
  }

  function clearSearch() {
    setQuery("");
    setFound(null);
  }

  // Search results narrowed by the status group, as the day is.
  const foundOrders = (found?.orders ?? []).filter((o) => !group || o.status_group === group);
  // The day's 3PL carriers, for the filter.
  const carriers = new Map(
    (day?.records ?? []).flatMap((r) => (r.carrier_id ? [[r.carrier_id, r.carrier_name]] : [])),
  );

  async function exportDay() {
    if (!day?.plan_date) return;
    try {
      const { blob, filename } = await downloadDeliveries(day.plan_date, depotZip);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const refreshButton = (
    <button
      className="btn"
      disabled={!day?.plan_date || refreshing}
      onClick={() => day?.plan_date && void refresh(day.plan_date)}
    >
      {refreshing ? t("deliveries.refreshing") : t("deliveries.refresh")}
    </button>
  );

  return (
    <Codes.Provider value={codes}>
      <section className="starter" style={{ maxWidth: 1280 }}>
        <div className="starter-card" style={{ maxWidth: "none" }}>
          <h1>{t("deliveries.title")}</h1>
          <p className="starter-desc">{t("deliveries.desc")}</p>

          <form
            className="config-row"
            style={{ alignItems: "flex-end" }}
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              void runSearch(query);
            }}
          >
            <label className="control" style={{ flex: 1 }}>
              <span className="control-label">{t("deliveries.searchLabel")}</span>
              <input
                type="search"
                value={query}
                placeholder={t("deliveries.searchHint")}
                onChange={(e) => setQuery(e.target.value)}
              />
            </label>
            <button className="btn" type="submit" disabled={searching}>
              {searching ? t("app.loading") : t("deliveries.search")}
            </button>
            {found && (
              <button className="btn btn-ghost" type="button" onClick={clearSearch}>
                {t("deliveries.clearSearch")}
              </button>
            )}
          </form>

          <div className="config-row" style={{ alignItems: "flex-end" }}>
            <label className="control">
              <span className="control-label">{t("deliveries.depot")}</span>
              <select
                value={depotZip}
                onChange={(e) => {
                  setDepotZip(e.target.value);
                  setPlanDate(undefined);
                }}
              >
                {DEPOTS.map((d) => (
                  <option key={d.zip} value={d.zip}>
                    {t(d.region)}
                  </option>
                ))}
              </select>
            </label>
            <label className="control">
              <span className="control-label">{t("deliveries.date")}</span>
              <input
                type="date"
                value={planDate ?? day?.plan_date ?? ""}
                onChange={(e) => e.target.value && setPlanDate(e.target.value)}
              />
            </label>
            <label className="control">
              <span className="control-label">{t("deliveries.carriedBy")}</span>
              <select value={carriedBy} onChange={(e) => setCarriedBy(e.target.value)}>
                <option value="">{t("deliveries.all")}</option>
                <option value="fleet">{t("deliveries.ownFleet")}</option>
                <option value="3pl">{t("deliveries.anyCarrier")}</option>
                {[...carriers].map(([id, name]) => (
                  <option key={id} value={id}>
                    {name ?? id}
                  </option>
                ))}
              </select>
            </label>
            <label className="control">
              <span className="control-label">{t("deliveries.group")}</span>
              <select
                value={group}
                onChange={(e) => {
                  setGroup(e.target.value as StatusGroup | "");
                  setStage("");
                }}
              >
                <option value="">{t("deliveries.all")}</option>
                {GROUPS.map((g) => (
                  <option key={g} value={g}>
                    {t(`deliveries.group.${g}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="control">
              <span className="control-label">{t("deliveries.col.stage")}</span>
              <select value={stage} onChange={(e) => setStage(e.target.value as DeliveryStage | "")}>
                <option value="">{t("deliveries.all")}</option>
                {stages.map((s) => (
                  <option key={s} value={s}>
                    {t(`deliveries.stage.${s}` as MsgKey)}
                  </option>
                ))}
              </select>
            </label>
            <label className="control">
              <span className="control-label">{t("deliveries.filter.code")}</span>
              <select value={code} onChange={(e) => setCode(e.target.value)}>
                <option value="">{t("deliveries.all")}</option>
                {codes.map((c) => (
                  <option key={c.code} value={c.code}>
                    {lang === "zh" ? c.label_zh : c.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="control">
              <span className="control-label">{t("deliveries.filter.source")}</span>
              <select value={source} onChange={(e) => setSource(e.target.value as StatusSource | "")}>
                <option value="">{t("deliveries.all")}</option>
                {SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {t(`deliveries.said.${s}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="control" style={{ flexDirection: "row", gap: 6 }}>
              <input
                type="checkbox"
                checked={attentionOnly}
                onChange={(e) => setAttentionOnly(e.target.checked)}
              />
              {t("deliveries.attentionOnly")}
            </label>
            <label className="control" style={{ flexDirection: "row", gap: 6 }}>
              <input
                type="checkbox"
                checked={podMissing}
                onChange={(e) => setPodMissing(e.target.checked)}
              />
              {t("deliveries.sum.pod_missing")}
            </label>
            <button
              className={followUps ? "btn" : "btn btn-ghost"}
              aria-pressed={followUps}
              onClick={() => setFollowUps(!followUps)}
            >
              {t("deliveries.followUps")}
            </button>
            {refreshButton}
            <button className="btn" disabled={!day?.plan_date} onClick={() => void exportDay()}>
              {t("deliveries.export")}
            </button>
          </div>

          {error && <p className="upload-status error">{error}</p>}
          {simulated && <p className="upload-status">{t("deliveries.simulated")}</p>}
          {day && day.unassigned_3pl.length > 0 && (
            <div role="alert" className="upload-status" style={notice}>
              {day.unassigned_3pl.map((u) => (
                <div key={`${u.plan_id}/${u.truck_id}`} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {t("deliveries.unassigned", { truck: u.truck_id, n: u.orders })}
                  <button className="btn btn-ghost" onClick={() => onOpenPlan(u.plan_id, u.truck_id)}>
                    {t("deliveries.openPlan")}
                  </button>
                </div>
              ))}
            </div>
          )}
          {failures.length > 0 && (
            <p className="upload-status error">
              {t("deliveries.routesFailed", {
                routes: failures.map((f) => f.route_id).join(", "),
                reason: failures[0].reason,
              })}
            </p>
          )}

          {found && (
            <SearchResults
              found={found}
              orders={foundOrders}
              onChanged={() => void runSearch(found.q)}
              onOpenPlan={onOpenPlan}
            />
          )}

          {!found && followUps && (
            <FollowUpsList depotZip={depotZip} shown={shown} onOpenPlan={onOpenPlan} />
          )}

          {!found && !followUps && day && (
            <>
              <ul aria-label={t("deliveries.summary")} style={summaryRow}>
                {SUMMARY.map((k) => (
                  <li key={k} style={summaryTile}>
                    <strong style={{ fontSize: 20 }}>{day.summary[k]}</strong>
                    <span style={muted}>{t(`deliveries.sum.${k}` as MsgKey)}</span>
                  </li>
                ))}
              </ul>
              <p style={muted}>
                {day.last_refresh
                  ? t("deliveries.lastRefresh", {
                      time: new Date(day.last_refresh.ran_at).toLocaleString(),
                    })
                  : t("deliveries.neverRefreshed")}
                {" · "}
                {day.last_run
                  ? t("deliveries.lastRun", { time: new Date(day.last_run.ran_at).toLocaleString() })
                  : t("deliveries.neverRun")}
              </p>
              {day.records[0] && (
                <p style={muted}>
                  {t("deliveries.timesIn", {
                    zone: zoneName(`${day.records[0].plan_date}T12:00:00Z`, day.records[0].timezone),
                  })}
                </p>
              )}
              {day.plan_date && day.shifts.length > 0 && (
                <ClockInOut shifts={day.shifts} day={day.plan_date} />
              )}
            </>
          )}

          {!found && !followUps && (
            <div style={tableWrap}>
              <table style={table}>
                <thead>
                  <tr>
                    <th style={th}>{t("deliveries.col.order")}</th>
                    <th style={th}>{t("deliveries.col.customer")}</th>
                    <th style={th}>{t("deliveries.col.drop")}</th>
                    <th style={th}>{t("deliveries.col.truck")}</th>
                    <th style={th}>{t("deliveries.col.window")}</th>
                    <th style={th}>{t("deliveries.col.pallets")}</th>
                    <th style={th}>{t("deliveries.col.stage")}</th>
                    <th style={th}>{t("deliveries.col.attention")}</th>
                    <th style={th}>{t("deliveries.col.pod")}</th>
                    <th style={th}>{t("deliveries.col.followUp")}</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {day && records.length === 0 && (
                    <tr>
                      <td style={td} colSpan={COLUMNS}>
                        {day.has_sent_routes && day.records.length === 0 ? (
                          <span style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            {t("deliveries.notRead")}
                            {refreshButton}
                          </span>
                        ) : (
                          t("deliveries.empty")
                        )}
                      </td>
                    </tr>
                  )}
                  {records.map((r) => (
                    <DeliveryRow
                      key={`${r.drop_key}/${r.order_no}`}
                      record={r}
                      tripmates={(day?.records ?? []).filter(
                        (o) => o.plan_id === r.plan_id && o.route_id === r.route_id && o !== r,
                      )}
                      onChanged={() => void reload()}
                      onOpenPlan={onOpenPlan}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </Codes.Provider>
  );
}

// Each driver's clock in and out, with the truck inspected and the hours between.
function ClockInOut({ shifts, day }: { shifts: DriverShift[]; day: string }) {
  const t = useT();
  const at = (iso: string | null, truck: string | null, zone: string) =>
    iso ? [clockOnDay(iso, zone, day), truck].filter(Boolean).join(" · ") : "—";
  const hours = (s: DriverShift) => {
    if (!s.clock_in_at || !s.clock_out_at) return "—";
    const min = Math.round((Date.parse(s.clock_out_at) - Date.parse(s.clock_in_at)) / 60000);
    if (min < 0) return "—";
    return t("deliveries.clock.duration", { h: Math.floor(min / 60), m: min % 60 });
  };
  return (
    <div style={tableWrap}>
      <table style={{ ...table, width: "auto" }} aria-label={t("deliveries.clock.title")}>
        <caption style={{ ...muted, textAlign: "left", paddingBottom: 4 }}>
          <strong>{t("deliveries.clock.title")}</strong> · {t("deliveries.clock.hint")}
        </caption>
        <thead>
          <tr>
            <th style={th}>{t("deliveries.clock.driver")}</th>
            <th style={th}>{t("deliveries.clock.in")}</th>
            <th style={th}>{t("deliveries.clock.out")}</th>
            <th style={th}>{t("deliveries.clock.hours")}</th>
          </tr>
        </thead>
        <tbody>
          {shifts.map((s) => (
            <tr key={s.driver_id}>
              <td style={td}>{s.driver_name ?? s.driver_id}</td>
              <td style={td}>{at(s.clock_in_at, s.clock_in_vehicle_name, s.timezone)}</td>
              <td style={td}>{at(s.clock_out_at, s.clock_out_vehicle_name, s.timezone)}</td>
              <td style={td}>{hours(s)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// When a record's stage was reached, if it has a time of its own.
function stageTime(r: DeliveryRecord): string | null {
  switch (r.stage) {
    case "left_depot":
      return r.en_route_at ?? r.picked_up_at;
    case "at_drop":
      return r.arrived_at;
    case "delivered":
    case "delivery_exception":
      return r.departed_at;
    default:
      return null;
  }
}

function DeliveryRow({
  record: r,
  tripmates,
  onChanged,
  onOpenPlan,
  lead,
  leadCols = 2,
}: {
  record: DeliveryRecord;
  tripmates: DeliveryRecord[];
  onChanged: () => void;
  onOpenPlan: (planId: string, truckId?: string) => void;
  // Leading cells, `leadCols` of them: a search's day and warehouse, say.
  lead?: ReactNode;
  leadCols?: number;
}) {
  const t = useT();
  const codeLabel = useCodeLabel();
  const [open, setOpen] = useState(false);
  const at = (iso: string | null) => clockOnDay(iso, r.timezone, r.plan_date);
  const when = stageTime(r);
  // How long ago only on the day itself: a day later it's just noise.
  const today = todayIn(r.timezone) === r.plan_date;
  return (
    <Fragment>
      <tr aria-label={r.order_no}>
        {lead}
        <td style={td}>{r.order_no}</td>
        <td style={td}>{r.customer_name ?? "—"}</td>
        <td style={{ ...td, whiteSpace: "nowrap" }}>{dropPlace(r)}</td>
        <td style={{ ...td, whiteSpace: "nowrap" }}>
          {r.vehicle_name ?? (r.carrier_type === "3pl" ? r.truck_id : null) ?? r.route_id}
          <div style={muted}>
            <span>
              {r.carrier_type === "3pl"
                ? (r.carrier_name ?? t("deliveries.noCarrier"))
                : t("deliveries.ownFleet")}
            </span>
            {" · "}
            <span>
              {r.execution_source === "manual"
                ? t("deliveries.source.manual")
                : t("deliveries.source.samsara")}
            </span>
          </div>
        </td>
        <td style={{ ...td, whiteSpace: "nowrap" }}>
          {r.window_open && r.deadline
            ? `${at(r.window_open)}–${at(r.deadline)}`
            : r.deadline
              ? t("deliveries.by", { time: at(r.deadline) })
              : r.window_open
                ? t("deliveries.after", { time: at(r.window_open) })
                : "—"}
        </td>
        <td style={td}>{r.pallets ?? "—"}</td>
        <td style={{ ...td, whiteSpace: "nowrap" }}>
          <span style={{ ...pill, ...STAGE_STYLE[r.stage] }}>
            {t(`deliveries.stage.${r.stage}` as MsgKey)}
          </span>
          {!r.closed_out_at && (
            <span style={openMark} title={t("deliveries.openHint")}>
              {t("deliveries.open")}
            </span>
          )}
          {/* Samsara or the carrier is named under the truck: only an override is marked. */}
          {r.status_source === "dispatcher" && (
            <span style={sourceMark}>{t("deliveries.said.dispatcher")}</span>
          )}
          {r.exception_code && <div>{codeLabel(r.exception_code)}</div>}
          <div style={muted}>
            {when
              ? `${at(when)}${r.closed_out_at || !today ? "" : ` · ${ago(t, when)}`}`
              : r.stage === "scheduled" && r.scheduled_arrival
                ? t("deliveries.eta", { time: at(r.scheduled_arrival) })
                : ""}
          </div>
        </td>
        <td style={td}>
          {r.attention.length > 0 ? (
            <ul aria-label={t("deliveries.col.attention")} style={reasons}>
              {r.attention.map((f) => (
                <li key={f}>{t(`deliveries.flag.${f}` as MsgKey)}</li>
              ))}
            </ul>
          ) : (
            r.reviewed_at && (
              <span style={muted} title={r.review_note ?? undefined}>
                {t("deliveries.reviewed")}
              </span>
            )
          )}
        </td>
        <td style={td}>
          {r.evidence === "pod" ? (
            <span style={{ ...pill, ...STAGE_STYLE.delivered }}>{t("deliveries.pod.driver")}</span>
          ) : r.evidence === "dispatcher" ? (
            <span style={{ ...pill, ...STAGE_STYLE.left_depot }}>{t("deliveries.pod.dispatcher")}</span>
          ) : r.evidence === "geofence" ? (
            <span style={{ ...pill, ...STAGE_STYLE.not_delivered }}>{t("deliveries.podMissing")}</span>
          ) : (
            "—"
          )}
        </td>
        <td style={{ ...td, whiteSpace: "nowrap" }}>
          {r.follow_up_open && r.disposition ? t(`deliveries.disp.${r.disposition}`) : "—"}
        </td>
        <td style={{ ...td, whiteSpace: "nowrap" }}>
          <button className="btn btn-ghost" aria-expanded={open} onClick={() => setOpen(!open)}>
            {t("deliveries.details")}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td style={td} colSpan={COLUMNS + (lead ? leadCols : 0)}>
            <DeliveryDetails
              record={r}
              tripmates={tripmates}
              onChanged={onChanged}
              onOpenPlan={onOpenPlan}
            />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

// What a search found: each order's attempts, newest first, as the day's rows
// with their day and warehouse; a drop with no record yet as its plan has it.
function SearchResults({
  found,
  orders,
  onChanged,
  onOpenPlan,
}: {
  found: DeliverySearch;
  orders: DeliverySearch["orders"];
  onChanged: () => void;
  onOpenPlan: (planId: string, truckId?: string) => void;
}) {
  const t = useT();
  if (found.orders.length === 0) {
    return (
      <p className="upload-status" role="status">
        {found.in_order_sync === false ? t("deliveries.notFoundAnywhere") : t("deliveries.notFound")}
      </p>
    );
  }
  const lead = (a: SearchAttempt) => (
    <>
      <td style={{ ...td, whiteSpace: "nowrap" }}>{a.plan_date}</td>
      <td style={{ ...td, whiteSpace: "nowrap" }}>
        {t(DEPOTS.find((d) => d.zip === a.depot_zip)?.region ?? "region.socal")}
      </td>
    </>
  );
  return (
    <div style={tableWrap}>
      <table style={table} aria-label={t("deliveries.searchResults")}>
        <thead>
          <tr>
            <th style={th}>{t("deliveries.date")}</th>
            <th style={th}>{t("deliveries.depot")}</th>
            <th style={th}>{t("deliveries.col.order")}</th>
            <th style={th}>{t("deliveries.col.customer")}</th>
            <th style={th}>{t("deliveries.col.drop")}</th>
            <th style={th}>{t("deliveries.col.truck")}</th>
            <th style={th}>{t("deliveries.col.window")}</th>
            <th style={th}>{t("deliveries.col.pallets")}</th>
            <th style={th}>{t("deliveries.col.stage")}</th>
            <th style={th}>{t("deliveries.col.attention")}</th>
            <th style={th}>{t("deliveries.col.pod")}</th>
            <th style={th}>{t("deliveries.col.followUp")}</th>
            <th style={th}></th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) =>
            o.attempts.flatMap((a) =>
              a.drops.map((d) =>
                d.record ? (
                  <DeliveryRow
                    key={`${a.plan_id}/${d.route_id}/${o.order_no}`}
                    record={d.record}
                    tripmates={[]}
                    onChanged={onChanged}
                    onOpenPlan={onOpenPlan}
                    lead={lead(a)}
                  />
                ) : (
                  <tr key={`${a.plan_id}/${d.route_id}/${o.order_no}`} aria-label={o.order_no}>
                    {lead(a)}
                    <td style={td}>{o.order_no}</td>
                    <td style={td}>—</td>
                    <td style={td}>—</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      {d.vehicle_name ?? d.truck_id ?? d.route_id}
                      <div style={muted}>
                        {d.carrier_type === "3pl"
                          ? (d.carrier_name ?? t("deliveries.noCarrier"))
                          : [t("deliveries.ownFleet"), d.driver_name].filter(Boolean).join(" · ")}
                      </div>
                    </td>
                    <td style={td}>—</td>
                    <td style={td}>—</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      <span style={{ ...pill, ...(STAGE_STYLE[d.status as DeliveryStage] ?? STAGE_STYLE.scheduled) }}>
                        {t(`status.${d.status}`)}
                      </span>
                    </td>
                    <td style={td}></td>
                    <td style={td}>—</td>
                    <td style={td}>—</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      <button
                        className="btn btn-ghost"
                        onClick={() => onOpenPlan(a.plan_id, d.truck_id ?? undefined)}
                      >
                        {t("deliveries.openPlan")}
                      </button>
                    </td>
                  </tr>
                ),
              ),
            ),
          )}
          {orders.length === 0 && (
            <tr>
              <td style={td} colSpan={COLUMNS + 2}>
                {t("deliveries.empty")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// The drop's city and ZIP, e.g. "Beverly Hills 90210"; the ZIP alone when its
// address is a ZIP only.
function dropPlace(r: DeliveryRecord): string {
  const parts = (r.dest_address ?? "").split(",").map((p) => p.trim());
  const city = parts.length >= 3 ? parts[parts.length - 2] : null;
  return [city, r.dest_zip].filter(Boolean).join(" ") || "—";
}

// How long ago, e.g. "12 min ago".
function ago(t: ReturnType<typeof useT>, iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return min < 60
    ? t("deliveries.ago.min", { n: min })
    : t("deliveries.ago.hour", { n: Math.round(min / 60) });
}

// An order opened: its timeline, its proof, its outcome as the dispatcher
// records it, its exception and next step, the dispatcher's proof, its
// history and its share link.
function DeliveryDetails({
  record: r,
  tripmates,
  onChanged,
  onOpenPlan,
}: {
  record: DeliveryRecord;
  tripmates: DeliveryRecord[];
  onChanged: () => void;
  onOpenPlan: (planId: string, truckId?: string) => void;
}) {
  const t = useT();
  const [shareUrl, setShareUrl] = useState(r.share_url);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const at = (iso: string | null) => clockOnDay(iso, r.timezone, r.plan_date);
  const where =
    r.pod_place ?? (r.pod_lat !== null ? `${r.pod_lat.toFixed(5)}, ${r.pod_lon?.toFixed(5)}` : null);

  async function copy() {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
  }

  async function renew() {
    if (!window.confirm(t("deliveries.newLinkConfirm"))) return;
    try {
      setShareUrl((await renewShareLink(r.order_no)).share_url);
      setCopied(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <section aria-label={r.order_no} style={detailsBox}>
      <ul style={timeline}>
        {r.picked_up_at && <li>{t("deliveries.ev.pickedUp", { time: at(r.picked_up_at) })}</li>}
        {r.en_route_at && <li>{t("deliveries.ev.enRoute", { time: at(r.en_route_at) })}</li>}
        <li>
          {r.execution_source === "manual"
            ? r.outcome
              ? t(r.departed_at ? "deliveries.ev.reportedAt" : "deliveries.ev.reported", {
                  stage: t(`deliveries.stage.${r.outcome}` as MsgKey),
                  time: at(r.departed_at),
                })
              : t("deliveries.ev.noReport")
            : r.arrived_at
            ? r.departed_at
              ? t("deliveries.ev.visit", {
                  arrived: at(r.arrived_at),
                  departed: at(r.departed_at),
                  min: r.on_site_min ?? "—",
                })
              : t("deliveries.ev.arrived", { time: at(r.arrived_at) })
            : r.marked_delivered_at
              ? t("deliveries.ev.notTracked")
              : r.closed_out_at || r.stage === "not_delivered"
                ? t("deliveries.ev.notReached")
                : t("deliveries.ev.notYet")}
        </li>
        <li>
          {r.pod_submitted_at
            ? t("deliveries.ev.pod", {
                time: at(r.pod_submitted_at),
                place: where ?? t("deliveries.ev.noPlace"),
              }) +
              (r.pod_distance_km !== null
                ? t("deliveries.ev.podDistance", { km: r.pod_distance_km })
                : "")
            : r.evidence === "dispatcher"
              ? t("deliveries.ev.dispatcherProof")
              : t("deliveries.ev.noPod")}
        </li>
        {r.issue && (
          <li>
            {t("deliveries.ev.problem", { issue: r.issue }) +
              (r.issue_note ? ` — ${r.issue_note}` : "")}
          </li>
        )}
        {!r.issue && r.issue_note && <li>{t("deliveries.ev.note", { note: r.issue_note })}</li>}
      </ul>
      {r.photos.length > 0 && (
        <div style={photoRow}>
          {r.photos.map((p) => (
            <StampedPhoto key={p.media_id} photo={p} onRemoved={onChanged} />
          ))}
        </div>
      )}
      {(r.attention.length > 0 || r.reviewed_at) && <ReviewForm record={r} onChanged={onChanged} />}
      {r.status_source === "dispatcher" && <OverrideForm record={r} onChanged={onChanged} />}
      <OutcomeForm record={r} onChanged={onChanged} />
      {FAILED.includes(r.stage) && <FollowUpForm record={r} onChanged={onChanged} />}
      <DispatcherProofForm record={r} tripmates={tripmates} onChanged={onChanged} />
      {r.execution_source === "manual" && <CarrierUpdateForm record={r} onChanged={onChanged} />}
      <AttemptHistory record={r} />
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={muted}>{t("deliveries.shareLink")}</span>
        {shareUrl ? <code style={{ fontSize: 12 }}>{shareUrl}</code> : t("deliveries.noLink")}
        <button className="btn" disabled={!shareUrl} onClick={() => void copy()}>
          {t("deliveries.copyLink")}
        </button>
        {copied && <span style={muted}>{t("deliveries.copied")}</span>}
        <button className="btn btn-ghost" disabled={!shareUrl} onClick={() => void renew()}>
          {t("deliveries.newLink")}
        </button>
        <button className="btn btn-ghost" onClick={() => onOpenPlan(r.plan_id, r.truck_id ?? undefined)}
        >
          {t("deliveries.openPlan")}
        </button>
      </div>
      {error && <p className="upload-status error">{error}</p>}
    </section>
  );
}

// An order's reasons for attention, marked reviewed with what was done about
// them; a new reason brings it back.
function ReviewForm({ record: r, onChanged }: { record: DeliveryRecord; onChanged: () => void }) {
  const t = useT();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function review() {
    setBusy(true);
    setError(null);
    try {
      await reviewDelivery(r, note.trim(), r.attention);
      setNote("");
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-label={t("deliveries.review.title")} style={proofBox}>
      <strong>{t("deliveries.review.title")}</strong>
      {r.reviewed_at && (
        <span style={muted}>
          {t("deliveries.review.done", {
            who: r.reviewed_by ?? "—",
            time: clockOnDay(r.reviewed_at, r.timezone, r.plan_date),
            note: r.review_note ?? "",
          })}
        </span>
      )}
      {r.attention.length > 0 && (
        <>
          <ul style={reasons}>
            {r.attention.map((f) => (
              <li key={f}>{t(`deliveries.flag.${f}` as MsgKey)}</li>
            ))}
          </ul>
          {r.attention.includes("samsara_disagrees") && (
            <span style={muted}>{t("deliveries.review.keepHint")}</span>
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              aria-label={t("deliveries.review.note")}
              placeholder={t("deliveries.review.note")}
              value={note}
              maxLength={1000}
              onChange={(e) => setNote(e.target.value)}
              style={{ flex: 1, minWidth: 200 }}
            />
            <button className="btn" disabled={!note.trim() || busy} onClick={() => void review()}>
              {t("deliveries.review.submit")}
            </button>
          </div>
        </>
      )}
      {error && <p className="upload-status error">{error}</p>}
    </section>
  );
}

// What a 3PL carrier reported for a whole trip, as the dispatcher records it,
// at a time at the warehouse (now when none is given). Each order's own
// outcome is recorded with Record outcome.
function CarrierUpdateForm({ record: r, onChanged }: { record: DeliveryRecord; onChanged: () => void }) {
  const t = useT();
  const [stage, setStage] = useState<ManualStage>("delivered");
  const [time, setTime] = useState("");
  const [note, setNote] = useState("");
  const { busy, error, act } = useAct(onChanged);
  const noteMissing = NEEDS_NOTE.includes(stage) && !note.trim();

  function record() {
    const update = {
      stage,
      ...(time && { at: instantOnDay(r.plan_date, time, r.timezone) }),
      ...(note.trim() && { note: note.trim() }),
    };
    void act(async () => {
      await recordTripUpdate(r.plan_id, r.route_id, update);
      setNote("");
    });
  }

  return (
    <section aria-label={t("deliveries.update.title")} style={proofBox}>
      <strong>{t("deliveries.update.title")}</strong>
      <span style={muted}>{t("deliveries.update.hint")}</span>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
        <label className="control">
          <span className="control-label">{t("deliveries.update.stage")}</span>
          <select value={stage} onChange={(e) => setStage(e.target.value as ManualStage)}>
            {MANUAL_STAGES.map((s) => (
              <option key={s} value={s}>
                {t(`deliveries.stage.${s}` as MsgKey)}
              </option>
            ))}
          </select>
        </label>
        <label className="control">
          <span className="control-label">{t("deliveries.update.at")}</span>
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </label>
        <label className="control">
          <span className="control-label">{t("deliveries.update.note")}</span>
          <input type="text" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
        </label>
        <button className="btn" disabled={busy || noteMissing} onClick={record}>
          {t("deliveries.update.trip")}
        </button>
      </div>
      {stage === "scheduled" && <span style={muted}>{t("deliveries.update.scheduledHint")}</span>}
      {noteMissing && <span style={muted}>{t("deliveries.update.noteHint")}</span>}
      {error && <p className="upload-status error">{error}</p>}
    </section>
  );
}

// A dispatcher's change: busy while it is sent, its refusal shown, the day
// read again after it.
function useAct(onChanged: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function act(action: () => Promise<unknown>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, act };
}

// Record an order's outcome: what happened, why it failed, what happens to its
// freight. On a Samsara route it overrides Samsara's and needs a reason (and,
// for delivered, a proof); on a 3PL route it is what the carrier reported.
function OutcomeForm({ record: r, onChanged }: { record: DeliveryRecord; onChanged: () => void }) {
  const t = useT();
  const codes = useContext(Codes);
  const codeLabel = useCodeLabel();
  const carrier = r.execution_source === "manual";
  const [stage, setStage] = useState<DeliveryOutcome | null>(null);
  const [code, setCode] = useState("");
  const [reason, setReason] = useState("");
  const [disposition, setDisp] = useState<Disposition | "">("");
  const [time, setTime] = useState("");
  const { busy, error, act } = useAct(onChanged);
  const choices = codes.filter(
    (c) => c.assignable && !c.retired && stage !== null && c.outcomes.includes(stage),
  );
  const picked = codes.find((c) => c.code === code);
  const provider = r.provider_stage ?? r.stage;
  const proved =
    r.evidence === "pod" ||
    r.photos.some((p) => p.source === "dispatcher" && PROOF_KINDS.includes(p.kind));
  const blocked: string | null = !stage
    ? null
    : stage !== "delivered" && !code
      ? t("deliveries.outcome.pickCode")
      : !carrier && stage === provider
        ? t("deliveries.outcome.same")
        : !carrier && !reason.trim()
          ? t("deliveries.outcome.needReason")
          : picked?.note_required && !reason.trim()
            ? t("deliveries.outcome.needNote", { code: codeLabel(code) })
            : stage === "delivered" && !carrier && !proved
              ? t("deliveries.markHint")
              : null;

  function pick(s: DeliveryOutcome) {
    setStage(s);
    setCode("");
    setDisp("");
  }

  function pickCode(c: string) {
    setCode(c);
    setDisp(codes.find((x) => x.code === c)?.default_disposition ?? "");
  }

  function save() {
    if (!stage) return;
    void act(async () => {
      await recordOutcome(r, {
        stage,
        ...(code && { exception_code: code }),
        ...(reason.trim() && { reason: reason.trim() }),
        ...(disposition && { disposition }),
        ...(carrier && time && { at: instantOnDay(r.plan_date, time, r.timezone) }),
      });
      setStage(null);
      setReason("");
    });
  }

  return (
    <section aria-label={t("deliveries.outcome.title")} style={proofBox}>
      <strong>{t("deliveries.outcome.title")}</strong>
      <span style={muted}>
        {carrier ? t("deliveries.outcome.hintCarrier") : t("deliveries.outcome.hintSamsara")}
      </span>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {OUTCOMES.map((s) => (
          <button
            key={s}
            className={stage === s ? "btn" : "btn btn-ghost"}
            aria-pressed={stage === s}
            onClick={() => pick(s)}
          >
            {t(`deliveries.stage.${s}` as MsgKey)}
          </button>
        ))}
      </div>
      {stage && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          {stage !== "delivered" && (
            <label className="control">
              <span className="control-label">{t("deliveries.outcome.code")}</span>
              <select value={code} onChange={(e) => pickCode(e.target.value)}>
                <option value="">{t("deliveries.outcome.pick")}</option>
                {choices.map((c) => (
                  <option key={c.code} value={c.code} title={c.use_when}>
                    {codeLabel(c.code)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="control" style={{ flex: 1, minWidth: 200 }}>
            <span className="control-label">
              {carrier ? t("deliveries.update.note") : t("deliveries.outcome.reason")}
            </span>
            <input
              type="text"
              value={reason}
              maxLength={1000}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          {stage !== "delivered" && (
            <label className="control">
              <span className="control-label">{t("deliveries.outcome.disposition")}</span>
              <select value={disposition} onChange={(e) => setDisp(e.target.value as Disposition | "")}>
                <option value="">—</option>
                {DISPOSITIONS.filter((d) => d !== "picked_up").map((d) => (
                  <option key={d} value={d}>
                    {t(`deliveries.disp.${d}`)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {carrier && (
            <label className="control">
              <span className="control-label">{t("deliveries.update.at")}</span>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </label>
          )}
          <button className="btn" disabled={busy || blocked !== null} onClick={save}>
            {t("deliveries.outcome.save")}
          </button>
        </div>
      )}
      {stage && !carrier && stage !== provider && (
        <span style={muted}>
          {t("deliveries.outcome.overrides", { stage: t(`deliveries.stage.${provider}` as MsgKey) })}
        </span>
      )}
      {blocked && <span style={muted}>{blocked}</span>}
      {error && <p className="upload-status error">{error}</p>}
    </section>
  );
}

// The dispatcher's outcome over Samsara's: who set it, when and why, what
// Samsara says now, and going back to Samsara's, with why.
function OverrideForm({ record: r, onChanged }: { record: DeliveryRecord; onChanged: () => void }) {
  const t = useT();
  const [reason, setReason] = useState("");
  const { busy, error, act } = useAct(onChanged);
  const provider = r.provider_stage ?? r.stage;
  return (
    <section aria-label={t("deliveries.override.title")} style={proofBox}>
      <strong>{t("deliveries.override.title")}</strong>
      <span>
        {t("deliveries.override.by", {
          who: r.override_by ?? r.marked_delivered_by ?? "—",
          time: clockOnDay(r.override_at ?? r.marked_delivered_at, r.timezone, r.plan_date),
          reason: r.override_reason ?? "—",
        })}
      </span>
      <span style={muted}>
        {provider === r.stage
          ? t("deliveries.override.agrees")
          : t("deliveries.override.samsaraSays", {
              stage: t(`deliveries.stage.${provider}` as MsgKey),
            })}
      </span>
      {r.execution_source !== "manual" && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            aria-label={t("deliveries.override.reason")}
            placeholder={t("deliveries.override.reason")}
            value={reason}
            maxLength={1000}
            onChange={(e) => setReason(e.target.value)}
            style={{ flex: 1, minWidth: 200 }}
          />
          <button
            className="btn btn-ghost"
            disabled={busy || !reason.trim()}
            onClick={() => void act(() => revertOutcome(r, reason.trim()))}
          >
            {t("deliveries.override.revert")}
          </button>
        </div>
      )}
      {error && <p className="upload-status error">{error}</p>}
    </section>
  );
}

// A failed attempt's exception code, changed without its outcome, and what
// happens to its freight next.
function FollowUpForm({ record: r, onChanged }: { record: DeliveryRecord; onChanged: () => void }) {
  const t = useT();
  const codes = useContext(Codes);
  const codeLabel = useCodeLabel();
  const [code, setCode] = useState("");
  const [codeNote, setCodeNote] = useState("");
  const [disposition, setDisp] = useState<Disposition | "">("");
  const [note, setNote] = useState("");
  const recode = useAct(onChanged);
  const dispose = useAct(onChanged);
  const choices = codes.filter(
    (c) => c.assignable && !c.retired && r.outcome !== null && c.outcomes.includes(r.outcome),
  );
  const picked = codes.find((c) => c.code === code);
  // Picked up only once it waits for the customer; cancelled says why.
  const offered = DISPOSITIONS.filter(
    (d) => d !== "picked_up" || r.disposition === "customer_pickup",
  );
  const noteMissing = disposition === "cancelled" && !note.trim();
  return (
    <section aria-label={t("deliveries.followUp.title")} style={proofBox}>
      <strong>{t("deliveries.followUp.title")}</strong>
      <span>{t("deliveries.followUp.code", { code: codeLabel(r.exception_code) || "—" })}</span>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <select
          aria-label={t("deliveries.outcome.code")}
          value={code}
          onChange={(e) => setCode(e.target.value)}
        >
          <option value="">{t("deliveries.outcome.pick")}</option>
          {choices.map((c) => (
            <option key={c.code} value={c.code} title={c.use_when}>
              {codeLabel(c.code)}
            </option>
          ))}
        </select>
        <input
          aria-label={t("deliveries.update.note")}
          placeholder={t("deliveries.update.note")}
          value={codeNote}
          maxLength={1000}
          onChange={(e) => setCodeNote(e.target.value)}
          style={{ flex: 1, minWidth: 160 }}
        />
        <button
          className="btn btn-ghost"
          disabled={
            recode.busy ||
            !code ||
            code === r.exception_code ||
            (picked?.note_required && !codeNote.trim())
          }
          onClick={() =>
            void recode
              .act(() => recodeDelivery(r, code, codeNote.trim() || undefined))
              .then((ok) => ok && (setCode(""), setCodeNote("")))
          }
        >
          {t("deliveries.followUp.recode")}
        </button>
      </div>
      {recode.error && <p className="upload-status error">{recode.error}</p>}
      <span>
        {r.disposition
          ? t("deliveries.followUp.now", { disposition: t(`deliveries.disp.${r.disposition}`) })
          : t("deliveries.followUp.none")}
        {r.disposition_note && <span style={muted}>{` — ${r.disposition_note}`}</span>}
      </span>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <select
          aria-label={t("deliveries.outcome.disposition")}
          value={disposition}
          onChange={(e) => setDisp(e.target.value as Disposition | "")}
        >
          <option value="">{t("deliveries.outcome.pick")}</option>
          {offered.map((d) => (
            <option key={d} value={d}>
              {t(`deliveries.disp.${d}`)}
            </option>
          ))}
        </select>
        <input
          aria-label={t("deliveries.followUp.note")}
          placeholder={t("deliveries.followUp.note")}
          value={note}
          maxLength={1000}
          onChange={(e) => setNote(e.target.value)}
          style={{ flex: 1, minWidth: 160 }}
        />
        <button
          className="btn"
          disabled={dispose.busy || !disposition || noteMissing}
          onClick={() =>
            disposition &&
            void dispose
              .act(() => setDisposition(r, disposition, note.trim() || undefined))
              .then((ok) => ok && (setDisp(""), setNote("")))
          }
        >
          {t("deliveries.followUp.save")}
        </button>
      </div>
      {noteMissing && <span style={muted}>{t("deliveries.followUp.noteCancel")}</span>}
      {dispose.error && <p className="upload-status error">{dispose.error}</p>}
    </section>
  );
}

// Every attempt of the order, numbered, each with its events: who changed
// what, from what, and why. Read when opened.
function AttemptHistory({ record: r }: { record: DeliveryRecord }) {
  const t = useT();
  const codeLabel = useCodeLabel();
  const [history, setHistory] = useState<OrderHistory | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggle() {
    setOpen(!open);
    if (open) return;
    setError(null);
    try {
      setHistory(await getOrderHistory(r.order_no));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const stageOf = (s: DeliveryStage | undefined | null) =>
    s ? t(`deliveries.stage.${s}` as MsgKey) : null;
  const m = history?.attempts.length ?? 0;
  return (
    <section aria-label={t("deliveries.history.title")}>
      <button className="btn btn-ghost" aria-expanded={open} onClick={() => void toggle()}>
        {t("deliveries.history.title")}
      </button>
      {open && history && (
        <ol style={{ ...timeline, listStyle: "none", paddingLeft: 0 }}>
          {[...history.attempts].reverse().map(({ attempt_no: n, record: a, events }) => (
            <li key={`${a.plan_id}/${a.drop_key}`} style={{ marginTop: 8 }}>
              <strong>{t("deliveries.history.attempt", { n, m })}</strong>
              {" · "}
              {[
                a.plan_date,
                stageOf(a.stage),
                a.exception_code && codeLabel(a.exception_code),
                a.disposition && t(`deliveries.disp.${a.disposition}`),
                a.plan_id === r.plan_id && a.drop_key === r.drop_key && t("deliveries.history.this"),
              ]
                .filter(Boolean)
                .join(" · ")}
              {events.length === 0 ? (
                <div style={muted}>{t("deliveries.history.empty")}</div>
              ) : (
                <ul style={timeline}>
                  {events.map((e, i) => {
                    const from = stageOf(e.previous?.stage);
                    const to = stageOf(e.stage);
                    return (
                      <li key={i}>
                        {[
                          new Date(e.at).toLocaleString(),
                          t(`deliveries.said.${e.source}`),
                          // A person's change names them; Samsara's reading is its own.
                          e.source !== "samsara" && e.entered_by,
                          t(`deliveries.history.kind.${e.kind}`),
                          to && (from && from !== to ? `${from} → ${to}` : to),
                          e.exception_code && codeLabel(e.exception_code),
                          e.disposition && t(`deliveries.disp.${e.disposition}`),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                        {(e.reason ?? e.note) && <span style={muted}>{` — ${e.reason ?? e.note}`}</span>}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          ))}
        </ol>
      )}
      {error && <p className="upload-status error">{error}</p>}
    </section>
  );
}

// The open follow-ups of every day at the warehouse, oldest first, narrowed by
// next step and by the day's filters; each next step can be set in its row.
function FollowUpsList({
  depotZip,
  shown,
  onOpenPlan,
}: {
  depotZip: string;
  shown: (r: DeliveryRecord) => boolean;
  onOpenPlan: (planId: string, truckId?: string) => void;
}) {
  const t = useT();
  const [disposition, setDisp] = useState<Disposition | "">("");
  const [list, setList] = useState<FollowUps | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      setList(await listFollowUps(depotZip, disposition || undefined));
    } catch (e) {
      setError(t("deliveries.loadFailed", { msg: e instanceof Error ? e.message : String(e) }));
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depotZip, disposition]);

  async function quick(r: DeliveryRecord, d: Disposition) {
    try {
      await setDisposition(r, d);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const rows = (list?.follow_ups ?? []).filter((f) => shown(f.record));
  return (
    <>
      <div className="config-row" style={{ alignItems: "flex-end" }}>
        <label className="control">
          <span className="control-label">{t("deliveries.outcome.disposition")}</span>
          <select value={disposition} onChange={(e) => setDisp(e.target.value as Disposition | "")}>
            <option value="">{t("deliveries.all")}</option>
            {OPEN_DISPOSITIONS.map((d) => (
              <option key={d} value={d}>
                {t(`deliveries.disp.${d}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error && <p className="upload-status error">{error}</p>}
      <div style={tableWrap}>
        <table style={table} aria-label={t("deliveries.followUps")}>
          <thead>
            <tr>
              <th style={th}>{t("deliveries.date")}</th>
              <th style={th}>{t("deliveries.followUps.attempt")}</th>
              <th style={th}>{t("deliveries.outcome.disposition")}</th>
              <th style={th}>{t("deliveries.col.order")}</th>
              <th style={th}>{t("deliveries.col.customer")}</th>
              <th style={th}>{t("deliveries.col.drop")}</th>
              <th style={th}>{t("deliveries.col.truck")}</th>
              <th style={th}>{t("deliveries.col.window")}</th>
              <th style={th}>{t("deliveries.col.pallets")}</th>
              <th style={th}>{t("deliveries.col.stage")}</th>
              <th style={th}>{t("deliveries.col.attention")}</th>
              <th style={th}>{t("deliveries.col.pod")}</th>
              <th style={th}>{t("deliveries.col.followUp")}</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {list && rows.length === 0 && (
              <tr>
                <td style={td} colSpan={COLUMNS + 3}>
                  {t("deliveries.followUps.empty")}
                </td>
              </tr>
            )}
            {rows.map(({ record: r, attempt_no, follow_up }) => (
              <DeliveryRow
                key={`${r.plan_id}/${r.drop_key}/${r.order_no}`}
                record={r}
                tripmates={[]}
                onChanged={() => void load()}
                onOpenPlan={onOpenPlan}
                leadCols={3}
                lead={
                  <>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{r.plan_date}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      {t("deliveries.followUps.attemptN", { n: attempt_no })}
                      {follow_up.redelivery_plan_date && (
                        <div style={muted}>
                          {t("deliveries.followUps.onPlan", { date: follow_up.redelivery_plan_date })}
                        </div>
                      )}
                    </td>
                    <td style={td}>
                      <select
                        aria-label={t("deliveries.outcome.disposition")}
                        value={follow_up.disposition}
                        onChange={(e) => void quick(r, e.target.value as Disposition)}
                      >
                        {DISPOSITIONS.filter(
                          (d) =>
                            d !== "cancelled" &&
                            (d !== "picked_up" || follow_up.disposition === "customer_pickup"),
                        ).map((d) => (
                          <option key={d} value={d}>
                            {t(`deliveries.disp.${d}`)}
                          </option>
                        ))}
                      </select>
                    </td>
                  </>
                }
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// The dispatcher's own proof for an order (or a photo of what went wrong):
// upload one. Record outcome marks it delivered on it. One file (a BOL for the
// whole trip) may go to the trip's other orders too; it is stored once.
function DispatcherProofForm({
  record: r,
  tripmates,
  onChanged,
}: {
  record: DeliveryRecord;
  tripmates: DeliveryRecord[];
  onChanged: () => void;
}) {
  const t = useT();
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<string>("signed_bol");
  const [also, setAlso] = useState<string[]>([]); // drop keys
  const { busy, error, act } = useAct(onChanged);
  // The trip's other drops with their orders: an upload goes on a drop, every order at it.
  const drops = new Map<string, string[]>();
  for (const o of tripmates) {
    if (o.drop_key === r.drop_key) continue;
    drops.set(o.drop_key, [...(drops.get(o.drop_key) ?? []), o.order_no]);
  }

  return (
    <section aria-label={t("deliveries.proof.title")} style={proofBox}>
      <strong>{t("deliveries.proof.title")}</strong>
      {r.evidence !== "pod" && <span style={muted}>{t("deliveries.proof.hint")}</span>}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
        <label className="control">
          <span className="control-label">{t("deliveries.proof.file")}</span>
          <input
            type="file"
            accept="image/jpeg,image/png,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <label className="control">
          <span className="control-label">{t("deliveries.proof.kind")}</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            {UPLOAD_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`deliveries.proof.kind.${k}` as MsgKey)}
              </option>
            ))}
          </select>
        </label>
        <button
          className="btn"
          disabled={!file || busy}
          onClick={() =>
            file &&
            void act(async () => {
              await uploadDeliveryProof(r.plan_id, [r.drop_key, ...also], file, kind);
              setAlso([]);
            })
          }
        >
          {busy ? t("deliveries.proof.uploading") : t("deliveries.proof.upload")}
        </button>
      </div>
      {drops.size > 0 && (
        <fieldset style={alsoBox}>
          <legend style={muted}>{t("deliveries.proof.also")}</legend>
          {[...drops].map(([key, orderNos]) => (
            <label key={key} style={{ display: "inline-flex", gap: 4, marginRight: 12 }}>
              <input
                type="checkbox"
                checked={also.includes(key)}
                onChange={(e) =>
                  setAlso(e.target.checked ? [...also, key] : also.filter((k) => k !== key))
                }
              />
              {orderNos.join(", ")}
            </label>
          ))}
        </fieldset>
      )}
      {error && <p className="upload-status error">{error}</p>}
    </section>
  );
}

// A stamped photo, or an uploaded PDF; it needs the login, so it is fetched
// rather than linked. The dispatcher may remove their own uploads.
function StampedPhoto({ photo, onRemoved }: { photo: ProofPhoto; onRemoved: () => void }) {
  const t = useT();
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pdf = photo.content_type === "application/pdf";

  async function remove() {
    if (!window.confirm(t("deliveries.proof.removeConfirm"))) return;
    try {
      await deleteDeliveryProof(photo.media_id);
      onRemoved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    let url: string | null = null;
    let alive = true;
    fetchDeliveryPhoto(photo.media_id)
      .then((blob) => {
        if (!alive) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [photo.media_id]);

  // An upload has no form field to name it: its kind does.
  const label =
    photo.label ??
    ((UPLOAD_KINDS as readonly string[]).includes(photo.kind)
      ? t(`deliveries.proof.kind.${photo.kind}` as MsgKey)
      : photo.kind);
  return (
    <figure style={{ margin: 0, width: 180 }}>
      {src && pdf ? (
        <a href={src} target="_blank" rel="noreferrer" style={{ ...photoImg, ...pdfTile }}>
          {t("deliveries.proof.openPdf")}
        </a>
      ) : src ? (
        <a href={src} target="_blank" rel="noreferrer">
          <img src={src} alt={label} title={photo.stamp_text} style={photoImg} />
        </a>
      ) : (
        <div style={{ ...photoImg, display: "grid", placeItems: "center" }}>
          {failed ? t("deliveries.photoFailed") : "…"}
        </div>
      )}
      <figcaption style={muted}>
        {label}
        {photo.source === "dispatcher" && (
          <>
            <br />
            {t("deliveries.proof.by", { who: photo.uploaded_by ?? "—" })} ·{" "}
            <button className="btn btn-ghost" style={{ padding: 0 }} onClick={() => void remove()}>
              {t("deliveries.proof.remove")}
            </button>
          </>
        )}
      </figcaption>
      {error && <p className="upload-status error">{error}</p>}
    </figure>
  );
}

const muted: CSSProperties = { opacity: 0.65, fontSize: 13 };
const detailsBox: CSSProperties = { display: "grid", gap: 12, padding: "8px 4px" };
const timeline: CSSProperties = { margin: 0, paddingLeft: 18, lineHeight: 1.7 };
const photoRow: CSSProperties = { display: "flex", gap: 12, flexWrap: "wrap" };
const proofBox: CSSProperties = {
  display: "grid",
  gap: 8,
  padding: 10,
  borderRadius: 8,
  border: "1px dashed rgba(0,0,0,0.2)",
};
const alsoBox: CSSProperties = { border: "none", margin: 0, padding: 0 };
const pdfTile: CSSProperties = { display: "grid", placeItems: "center", textDecoration: "none" };
// The whole photo: the watermark is along its bottom edge.
const photoImg: CSSProperties = {
  width: 180,
  height: 240,
  objectFit: "contain",
  background: "rgba(0,0,0,0.04)",
  borderRadius: 6,
  border: "1px solid rgba(0,0,0,0.12)",
};
const summaryRow: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
  listStyle: "none",
  padding: 0,
  margin: "16px 0 4px",
};
const summaryTile: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minWidth: 96,
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid rgba(0,0,0,0.1)",
};
const tableWrap: CSSProperties = { overflowX: "auto", marginTop: 12 };
const table: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: CSSProperties = {
  textAlign: "left",
  padding: "6px 10px",
  borderBottom: "1px solid rgba(0,0,0,0.15)",
  whiteSpace: "nowrap",
};
const td: CSSProperties = { padding: "6px 10px", borderBottom: "1px solid rgba(0,0,0,0.06)" };
const pill: CSSProperties = {
  display: "inline-block",
  padding: "1px 8px",
  borderRadius: 999,
  fontSize: 12,
  whiteSpace: "nowrap",
};
// Whose word the stage is, beside it.
const sourceMark: CSSProperties = { marginLeft: 6, fontSize: 11, opacity: 0.7 };
// Open: its route still runs, so the stage may change.
const openMark: CSSProperties = {
  marginLeft: 6,
  fontSize: 11,
  opacity: 0.7,
  border: "1px dashed rgba(0,0,0,0.3)",
  borderRadius: 999,
  padding: "0 6px",
};
// Something to do, not a failure.
const notice: CSSProperties = {
  display: "grid",
  gap: 4,
  padding: "8px 12px",
  borderRadius: 8,
  background: "rgba(234,88,12,0.08)",
  color: "#9a3412",
};
const reasons: CSSProperties = { margin: 0, paddingLeft: 16, fontSize: 13 };
